// batch-70 vakantiecampagne (FO-525) - Vakantie 2027 als één aanvraag.
//
// De gedeelde schema's, DTO's en vaste teksten van de vijfstaps-wizard (spelregels,
// saldo en keuzes, advies en doorrekening, verlof 2027, controleren en indienen) en
// van de collectieve beoordeling in de backoffice. De API valideert met dezelfde
// schema's als de formulieren (één bron, inline veldfouten).
//
// Besluiten zijn intern tot "Uitkomst versturen" (§E19): het DTO voor de medewerker
// toont een besluit pas als `uitkomstVerstuurd` waar is, anders "In beoordeling".

import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'
import { SPAAR_KEUZES, type SpaarKeuze } from './werktijd-sparen'
import type { JaaroverzichtDTO } from './verlof-jaaroverzicht'
import { CAMPAGNE_AFWIJS_REDEN, type CampagneAfwijsReden, type CampagneVoorkeurBesluit } from './campagne'

// ─── Fase, keuzebesluit, soort wens ──────────────────────────────────────────

export const CAMPAGNE_FASE = ['VERZAMELEN', 'BEOORDELEN', 'UITKOMST_VERSTUURD'] as const
export type CampagneFase = (typeof CAMPAGNE_FASE)[number]

export const CAMPAGNE_FASE_LABELS: Record<CampagneFase, string> = {
  VERZAMELEN: 'Verzamelen',
  BEOORDELEN: 'Samen beoordelen',
  UITKOMST_VERSTUURD: 'Uitkomst verstuurd',
}

/**
 * De fase zoals hij geldt (§E17): na het versturen van de uitkomst UITKOMST_VERSTUURD,
 * na de deadline BEOORDELEN, anders de opgeslagen fase (VERZAMELEN, of BEOORDELEN als
 * HR de beoordeling eerder startte).
 */
export function effectieveCampagneFase(
  c: { fase: CampagneFase; deadline: Date | string; uitkomstVerstuurdOp: Date | string | null },
  nu: Date = new Date(),
): CampagneFase {
  if (c.uitkomstVerstuurdOp != null || c.fase === 'UITKOMST_VERSTUURD') return 'UITKOMST_VERSTUURD'
  if (new Date(c.deadline).getTime() < nu.getTime()) return 'BEOORDELEN'
  return c.fase
}

/**
 * Collectief beoordelen (§E19): een campagne met een uitkomst-deadline verzamelt eerst
 * en verstuurt de uitkomst in één keer. Tot dat moment zijn besluiten intern. Na het
 * versturen (en bij een campagne zonder uitkomst-deadline, de werkwijze van vóór
 * batch-70) gaat een besluit direct naar de medewerker.
 */
export function isUitkomstCollectief(c: { uitkomstDeadline: Date | string | null; uitkomstVerstuurdOp: Date | string | null }): boolean {
  return c.uitkomstDeadline != null && c.uitkomstVerstuurdOp == null
}

export const CAMPAGNE_KEUZE_BESLUIT = ['ADVIES', 'ZELF', 'GEHOUDEN', 'GEEN'] as const
export type CampagneKeuzeBesluit = (typeof CAMPAGNE_KEUZE_BESLUIT)[number]

export const CAMPAGNE_KEUZE_BESLUIT_LABELS: Record<CampagneKeuzeBesluit, string> = {
  ADVIES: 'Advies gevolgd',
  ZELF: 'Zelf ingesteld',
  GEHOUDEN: 'Keuzes gehouden',
  GEEN: 'Nog geen keuze',
}

export const CAMPAGNE_WENS_SOORT = ['PERIODE', 'LOSSE_DAG', 'HALVE_DAG'] as const
export type CampagneWensSoort = (typeof CAMPAGNE_WENS_SOORT)[number]

export const CAMPAGNE_WENS_SOORT_LABELS: Record<CampagneWensSoort, string> = {
  PERIODE: 'Periode',
  LOSSE_DAG: 'Losse dag',
  HALVE_DAG: 'Halve dag',
}

export const CAMPAGNE_DAGDEEL = ['OCHTEND', 'MIDDAG'] as const
export type CampagneDagdeel = (typeof CAMPAGNE_DAGDEEL)[number]

export const CAMPAGNE_DAGDEEL_LABELS: Record<CampagneDagdeel, string> = { OCHTEND: 'Ochtend', MIDDAG: 'Middag' }

/** De standaardtijden van een halve dag (op het kwartier, §6.3); de uren volgen het rooster. */
export const CAMPAGNE_DAGDEEL_TIJDEN: Record<CampagneDagdeel, { startTijd: string; eindTijd: string }> = {
  OCHTEND: { startTijd: '08:00', eindTijd: '12:00' },
  MIDDAG: { startTijd: '12:30', eindTijd: '16:30' },
}

/** Een periode krijgt een prioriteit vanaf zoveel werkdagen (§A3). */
export const CAMPAGNE_PRIORITEIT_VANAF_WERKDAGEN = 3

/** Losse dagen vóór deze datum (dag en maand) mogen vervroegd worden verstuurd (§E19, A6). */
export const CAMPAGNE_VERVROEGD_TOT = { maand: 4, dag: 1 } as const

// ─── Status van een wens ─────────────────────────────────────────────────────

/** De statusset van §D14; INGEDIEND = nog niet beoordeeld (besluit null). */
export const CAMPAGNE_WENS_STATUS = [
  'CONCEPT',
  'INGEDIEND',
  'IN_OVERLEG',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'WACHTLIJST',
  'VERVANGEN',
  'INGETROKKEN',
] as const
export type CampagneWensStatus = (typeof CAMPAGNE_WENS_STATUS)[number]

export const CAMPAGNE_WENS_STATUS_LABELS: Record<CampagneWensStatus, string> = {
  CONCEPT: 'Concept',
  INGEDIEND: 'Ingediend',
  IN_OVERLEG: 'In overleg',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD: 'Afgekeurd',
  WACHTLIJST: 'Wachtlijst',
  VERVANGEN: 'Vervangen',
  INGETROKKEN: 'Ingetrokken',
}

/** Status uit het besluit (backoffice: altijd het echte besluit). */
export function wensStatusUitBesluit(besluit: CampagneVoorkeurBesluit | null, concept: boolean): CampagneWensStatus {
  if (besluit == null) return concept ? 'CONCEPT' : 'INGEDIEND'
  if (besluit === 'AFGEWEZEN') return 'AFGEKEURD'
  return besluit
}

/**
 * Wat de MEDEWERKER ziet (§E19): het besluit pas als de uitkomst is verstuurd; daarvoor
 * "In beoordeling". Vervangen en ingetrokken zijn zijn eigen handelingen en zichtbaar.
 */
export function wensStatusVoorMedewerker(w: {
  besluit: CampagneVoorkeurBesluit | null
  uitkomstVerstuurd: boolean
  concept: boolean
}): { status: CampagneWensStatus | 'IN_BEOORDELING'; label: string } {
  if (w.besluit === 'VERVANGEN' || w.besluit === 'INGETROKKEN') {
    return { status: w.besluit, label: CAMPAGNE_WENS_STATUS_LABELS[w.besluit] }
  }
  if (w.concept) return { status: 'CONCEPT', label: CAMPAGNE_WENS_STATUS_LABELS.CONCEPT }
  if (w.besluit == null || !w.uitkomstVerstuurd) return { status: 'IN_BEOORDELING', label: 'In beoordeling' }
  const status = wensStatusUitBesluit(w.besluit, false)
  return { status, label: CAMPAGNE_WENS_STATUS_LABELS[status] }
}

// ─── Opbouwkeuzes TVT/ATV ────────────────────────────────────────────────────

export const opbouwKeuzeSchema = z
  .object({
    atv: z.boolean(),
    overuren: z.enum(SPAAR_KEUZES),
    reistijd: z.enum(SPAAR_KEUZES),
  })
  .strict()
export type OpbouwKeuzeDTO = z.infer<typeof opbouwKeuzeSchema>

/** Uit / Half / Volledig (spec §A2), in de woorden van de woordenlijst. */
export const OPBOUW_KEUZE_STAND_LABELS: Record<SpaarKeuze, string> = {
  UITBETALEN: 'Uit (uitbetalen)',
  HALF: 'Half',
  ALLES: 'Volledig',
}

// ─── Advies: de vaste tekstset (geen vrij gegenereerde tekst) ────────────────

export const ADVIES_REDEN_CODES = [
  'TE_KORT_IN_DIENST',
  'GEWOON_NIET_OP',
  'ATV_NIET_GEBRUIKT',
  'ATV_NODIG_VOOR_TVT',
  'TVT_WERK_HALF',
  'TVT_WERK_UIT',
  'TVT_REIS_HALF',
  'TVT_REIS_UIT',
  'ZONDER_ATV_GEEN_TVT',
  'ATV_NIET_ADVISEERBAAR',
  'HOUDEN',
] as const
export type AdviesRedenCode = (typeof ADVIES_REDEN_CODES)[number]

export interface AdviesRedenDTO {
  code: AdviesRedenCode
  /** De echte getallen: uren, procenten (0-100), jaartallen, maanden. */
  waarden: Record<string, number>
}

/**
 * De zinnen van het advies (§C11), met `{naam}` voor de echte getallen. Uren worden
 * bij het tonen op een half uur afgerond. Geen em-dash, je-vorm, STREEP-copy.
 */
export const ADVIES_REDEN_TEKSTEN: Record<AdviesRedenCode, string> = {
  TE_KORT_IN_DIENST:
    'Je bent dit jaar {maanden} maanden in dienst. Voor een advies kijken we naar minstens {minimum} maanden; je ziet daarom alleen wat je keuzes in uren betekenen.',
  GEWOON_NIET_OP:
    'Je nam in {jaar} {opgenomen} uur verlof op, alle potjes samen. Van je gewone verlof ({beschikbaar} uur) houd je eind {jaar} nog {overEind} uur over, en in {volgendJaar} komt er weer {nieuw} uur bij. Met ATV en TVT erbij spaar je uren die je waarschijnlijk niet opneemt.',
  ATV_NIET_GEBRUIKT:
    'Je ATV had je dit jaar nauwelijks nodig: je verlof paste binnen je gewone verlof en je gespaarde uren. Eind {jaar} staat er {overEind} uur ATV die je niet gebruikt.',
  ATV_NODIG_VOOR_TVT:
    'Je ATV had je dit jaar nauwelijks nodig (eind {jaar} staat er {overEind} uur). Zonder ATV kun je alleen geen overuren of reistijd sparen, en je gespaarde uren gebruik je wel: houd ATV daarom aan.',
  TVT_WERK_HALF:
    'Van je gespaarde overuren ({beschikbaar} uur) had je er naast je gewone verlof {gebruikt} nodig ({pct}%). Half sparen past daar beter bij: de andere helft krijg je uitbetaald.',
  TVT_WERK_UIT:
    'Van je gespaarde overuren ({beschikbaar} uur) had je er naast je gewone verlof {gebruikt} nodig ({pct}%). Uitbetalen past daar beter bij: je overuren komen dan bij je salaris.',
  TVT_REIS_HALF:
    'Van je gespaarde reistijd ({beschikbaar} uur) had je er naast je gewone verlof {gebruikt} nodig ({pct}%). Half sparen past daar beter bij: de andere helft krijg je uitbetaald.',
  TVT_REIS_UIT:
    'Van je gespaarde reistijd ({beschikbaar} uur) had je er naast je gewone verlof {gebruikt} nodig ({pct}%). Uitbetalen past daar beter bij: je reisuren komen dan bij je salaris.',
  ZONDER_ATV_GEEN_TVT:
    'Zonder ATV kun je geen overuren of reistijd sparen: die worden dan uitbetaald in de maand dat je ze maakt. Wil je wel blijven sparen, houd dan ATV aan.',
  ATV_NIET_ADVISEERBAAR:
    'ATV staat bij jou aan, maar levert bij je contract geen ATV-dagen op. Daar geven we geen advies over; HR vertelt je er graag meer over.',
  HOUDEN:
    'Je nam in {jaar} {opgenomen} uur verlof op en gebruikte je gewone verlof voor {pct}%. Je keuzes passen bij hoe je je verlof gebruikt: houd ze zoals ze zijn.',
}

/** Uren voor in een zin: afgerond op een half uur, NL-notatie ("144", "8,5"). */
export function formatAdviesUren(uren: number): string {
  const r = Math.round(uren * 2) / 2
  return r.toLocaleString('nl-NL', { maximumFractionDigits: 1 })
}

/** Vul een redenzin met de echte getallen. Uren-sleutels worden op 0,5 afgerond. */
export function renderAdviesReden(r: AdviesRedenDTO): string {
  const urenSleutels = new Set(['opgenomen', 'beschikbaar', 'overEind', 'nieuw', 'gebruikt'])
  return ADVIES_REDEN_TEKSTEN[r.code].replace(/\{(\w+)\}/g, (_, sleutel: string) => {
    const v = r.waarden[sleutel]
    if (v == null) return '?'
    return urenSleutels.has(sleutel) ? formatAdviesUren(v) : String(v)
  })
}

export const ADVIES_CATEGORIEEN = ['TE_KORT_IN_DIENST', 'ALLES_UIT', 'MINDER_SPAREN', 'HOUDEN'] as const
export type AdviesCategorie = (typeof ADVIES_CATEGORIEEN)[number]

/** De kop van het advies. */
export const ADVIES_CATEGORIE_KOPPEN: Record<AdviesCategorie, string> = {
  TE_KORT_IN_DIENST: 'Te kort in dienst voor een advies',
  ALLES_UIT: 'Ons advies: zet ATV en TVT uit',
  MINDER_SPAREN: 'Ons advies: spaar minder',
  HOUDEN: 'Ons advies: houd je keuzes',
}

// ─── Doorrekening (vorm gedeeld met `berekenDoorrekening2027` in @streep/utils) ─

export type AtvUitVariant = 'B_38_UUR' | 'A_40_UUR_UITBETAALD'

export type JaaroverzichtPotType = 'WETTELIJK' | 'BOVENWETTELIJK' | 'ATV' | 'TVT_WERK' | 'TVT_REIS'

/** Wat de doorrekening van het nieuwe jaar nodig heeft; de wizard rekent "zelf instellen" er live mee. */
export interface DoorrekeningInvoerDTO {
  eindJaar: Record<JaaroverzichtPotType, { eind: number; vervaltBijJaareinde: number | null }>
  contractUren: number
  atvMogelijk: boolean
  beleid: { wettelijkFactor: number; bovenwettelijkFactor: number; atvUrenPerJaar: number; tvtMaxOverdrachtUren: number }
  verwacht: { overuren: number; reisuren: number }
  /** Patroon van dit jaar; `geplandOverig` = ander verlof in het nieuwe jaar (buiten deze campagne). */
  opname: { patroon: number; geplandOverig: number }
  atvUitVariant: AtvUitVariant
  /** De keuzes die in december van dit jaar gelden: nodig voor het effect van een TVT-ingang per 1 december. */
  huidigDecember: OpbouwKeuzeDTO
}

export interface Doorrekening2027DTO {
  start: Record<JaaroverzichtPotType, number>
  startTotaal: number
  erbij: { wettelijk: number; bovenwettelijk: number; atv: number; tvtWerk: number; tvtReis: number; totaal: number; gewoon: number }
  uitbetaald: {
    atv: number
    overuren: number
    reisuren: number
    totaal: number
    overuurGeldFactor: number
    reisGeldFactor: number
  }
  verwachteOpname: number
  eind: number
  atvPerMaand: number
}

// ─── Spelregels (stap 0) ─────────────────────────────────────────────────────

export interface VakantieSpelregelDTO {
  /** LET_OP = een regel met een uitroepteken (te laat, wijzigen = nieuwe aanvraag). */
  soort: 'INFO' | 'LET_OP'
  tekst: string
}

const NL_LANGE_DATUM = new Intl.DateTimeFormat('nl-NL', { timeZone: 'Europe/Amsterdam', day: 'numeric', month: 'long', year: 'numeric' })

/** De vijf vaste spelregels (§A0), met de echte datums van de campagne. */
export function vakantieSpelregels(p: { uitkomstDeadline: Date | string | null }): VakantieSpelregelDTO[] {
  const uitkomst = p.uitkomstDeadline ? NL_LANGE_DATUM.format(new Date(p.uitkomstDeadline)) : null
  return [
    {
      soort: 'INFO',
      tekst: `We verzamelen alle aanvragen en beoordelen ze samen. Je hoort de uitkomst in één keer${uitkomst ? `, uiterlijk ${uitkomst}` : ''}.`,
    },
    {
      soort: 'LET_OP',
      tekst: 'Niet of te laat indienen kan betekenen dat je het verlof dat je wilt niet kunt krijgen. Wat op tijd binnen is, gaat voor.',
    },
    {
      soort: 'INFO',
      tekst: 'Losse dagen kun je hier ook al indienen. Een trouwerij, verhuizing of lang weekend: hoe eerder het binnen is, hoe groter de kans dat het past.',
    },
    {
      soort: 'INFO',
      tekst: 'Te veel aanvragen in dezelfde periode? Dan kijken we samen met jullie wat mogelijk is en wat niet.',
    },
    {
      soort: 'LET_OP',
      tekst: 'Dien volledig en kloppend in. Verlengen, wijzigen of aanpassen geldt als een nieuwe aanvraag. Was het al goedgekeurd, dan kan het door de wijziging alsnog worden afgekeurd.',
    },
  ]
}

/** De standaard campagnetekst (seed-default van `Campagne.spelregels`); HR kan hem aanpassen. */
export const STANDAARD_VAKANTIE_SPELREGELS =
  'In één aanvraag geef je je keuze voor TVT en ATV door en je verlofwensen voor het nieuwe jaar. Het duurt ongeveer vijf minuten; je kunt tussendoor stoppen, je concept wordt bewaard.'

// ─── Wensen: invoer (wizard) ─────────────────────────────────────────────────

const KWARTIER = /^([01]\d|2[0-3]):(00|15|30|45)$/

export const campagneWensInputSchema = z
  .object({
    soort: z.enum(CAMPAGNE_WENS_SOORT, { error: 'Kies periode, losse dag of halve dag' }),
    startDatum: z.string().datetime({ error: 'Kies een begindatum' }),
    eindDatum: z.string().datetime({ error: 'Kies een einddatum' }),
    /** Alleen bij een halve dag. */
    dagdeel: z.enum(CAMPAGNE_DAGDEEL).optional(),
    startTijd: z.string().regex(KWARTIER, 'Kies een tijd op het kwartier').optional(),
    eindTijd: z.string().regex(KWARTIER, 'Kies een tijd op het kwartier').optional(),
    omschrijving: z
      .string({ error: 'Geef een omschrijving' })
      .trim()
      .min(2, 'Geef een omschrijving (bijvoorbeeld "zomer met het gezin")')
      .max(120, 'De omschrijving is te lang (maximaal 120 tekens)'),
    opmerking: z.string().trim().max(500, 'De opmerking is te lang (maximaal 500 tekens)').optional(),
    /** Alleen voor een periode van minstens drie werkdagen; 1 = belangrijkst. */
    prioriteit: z.number().int().min(1).max(20).optional(),
  })
  .strict()
  .superRefine((w, ctx) => {
    if (new Date(w.eindDatum).getTime() < new Date(w.startDatum).getTime()) {
      ctx.addIssue({ code: 'custom', path: ['eindDatum'], message: 'De einddatum ligt voor de begindatum' })
    }
    if (w.soort !== 'PERIODE' && w.startDatum.slice(0, 10) !== w.eindDatum.slice(0, 10)) {
      ctx.addIssue({ code: 'custom', path: ['eindDatum'], message: 'Een losse of halve dag is één dag' })
    }
    if (w.soort === 'HALVE_DAG' && !w.dagdeel) {
      ctx.addIssue({ code: 'custom', path: ['dagdeel'], message: 'Kies ochtend of middag' })
    }
    if (w.startTijd && w.eindTijd && w.eindTijd <= w.startTijd) {
      ctx.addIssue({ code: 'custom', path: ['eindTijd'], message: 'De eindtijd ligt voor de begintijd' })
    }
  })
export type CampagneWensInput = z.infer<typeof campagneWensInputSchema>

/** Wanneer een lagere TVT-keuze ingaat (§A2, A9). ATV gaat altijd per 1 januari. */
export const CAMPAGNE_TVT_INGANGEN = ['DECEMBER', 'JANUARI'] as const
export type CampagneTvtIngang = (typeof CAMPAGNE_TVT_INGANGEN)[number]

/** De keuze voor 2027: advies overnemen, zelf instellen of houden (§A2, §C13). */
export const campagneKeuzeInputSchema = z.discriminatedUnion('besluit', [
  z.object({ besluit: z.literal('ADVIES'), tvtIngang: z.enum(CAMPAGNE_TVT_INGANGEN).optional() }).strict(),
  z.object({ besluit: z.literal('ZELF'), keuzes: opbouwKeuzeSchema, tvtIngang: z.enum(CAMPAGNE_TVT_INGANGEN).optional() }).strict(),
  z.object({ besluit: z.literal('GEHOUDEN') }).strict(),
])
export type CampagneKeuzeInput = z.infer<typeof campagneKeuzeInputSchema>

/** Stap 4: alles in één transactie (§A4). */
export const campagneIndienenSchema = z
  .object({
    keuze: campagneKeuzeInputSchema,
    wensen: z.array(campagneWensInputSchema).max(40, 'Te veel wensen in één aanvraag'),
    /** "Ik heb (nog) geen verlof": het verlofdeel is leeg, de keuzes blijven verplicht. */
    geenVerlof: z.boolean().default(false),
    geenVerlofOpmerking: z.string().trim().max(500).optional(),
    /** Het moment waarop de wizard de cijfers liet zien (voor de auditkopie). */
    gezienOp: z.string().datetime().optional(),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.geenVerlof && d.wensen.length > 0) {
      ctx.addIssue({ code: 'custom', path: ['wensen'], message: 'Je koos "geen verlof": haal de wensen weg of zet die keuze uit' })
    }
    if (!d.geenVerlof && d.wensen.length === 0) {
      ctx.addIssue({ code: 'custom', path: ['wensen'], message: 'Voeg minstens één wens toe, of kies "Ik heb (nog) geen verlof"' })
    }
  })
export type CampagneIndienenInput = z.infer<typeof campagneIndienenSchema>

/** Een concept bewaren: dezelfde velden, nog niet compleet. */
export const campagneConceptSchema = z
  .object({
    keuze: campagneKeuzeInputSchema.optional(),
    wensen: z.array(campagneWensInputSchema).max(40),
    geenVerlof: z.boolean().default(false),
  })
  .strict()
export type CampagneConceptInput = z.infer<typeof campagneConceptSchema>

/** Voorvertoning van stap 3 (uren, saldo na, bezetting) met de keuze van het scenario. */
export const campagneWensVoorvertoningSchema = z
  .object({
    wensen: z.array(campagneWensInputSchema).min(1).max(40),
    /** ATV in het nieuwe jaar volgens de gekozen keuze (telt in het saldo na). */
    atvNieuwJaar: z.boolean().optional(),
  })
  .strict()
export type CampagneWensVoorvertoningInput = z.infer<typeof campagneWensVoorvertoningSchema>

// ─── Wensen: DTO's ───────────────────────────────────────────────────────────

/** Een wens zoals de medewerker hem ziet (portal). Besluit pas zichtbaar na de uitkomst. */
export interface CampagneWensDTO {
  id: string
  soort: CampagneWensSoort
  startDatum: string
  eindDatum: string
  dagdeel: CampagneDagdeel | null
  startTijd: string | null
  eindTijd: string | null
  omschrijving: string
  opmerking: string | null
  prioriteit: number | null
  uren: number
  /** Status voor de medewerker: tot de uitkomst "In beoordeling" (§E19). */
  status: CampagneWensStatus | 'IN_BEOORDELING'
  statusLabel: string
  /** De reden, alleen als het besluit al naar de medewerker is verstuurd. */
  besluitReden: string | null
  uitkomstVerstuurd: boolean
  teLaat: boolean
  ingediendOp: string | null
  vervangtVoorkeurId: string | null
  /** Was de wens die deze vervangt al goedgekeurd (en verstuurd)? Dan blijft die staan tot het nieuwe besluit. */
  vervangtGoedgekeurde: boolean
  leaveRequestId: string | null
}

export interface CampagneWensVoorvertoningRegelDTO {
  /** Index in de verstuurde lijst. */
  index: number
  werkdagen: number
  uren: number
  /** Verwacht saldo na deze wens (gesorteerd op datum), live uit het ledger. */
  saldoNa: number
  toon: 'GROEN' | 'ORANJE' | 'ROOD'
  bezetting: Array<{ klantNaam: string; aantal: number; maxTegelijk: number; conflict: boolean; week: string }>
  overlappingen: Array<{ van: string; tot: string; statusLabel: string }>
  /** Prioriteit hoort bij deze wens (periode van minstens drie werkdagen). */
  prioriteitVanToepassing: boolean
}

export interface CampagneWensVoorvertoningDTO {
  regels: CampagneWensVoorvertoningRegelDTO[]
  totaalUren: number
  saldoEind: number
  onderVoorbehoud: boolean
  bezettingOnbekend: boolean
  /** Fouten per wens (buiten doelperiode, overlap, te veel periodes of losse dagen). */
  fouten: Array<{ index: number; veld: string; melding: string }>
}

// ─── De aanvraag voor de medewerker (stap 0 t/m 5) ───────────────────────────

export interface VakantieAanvraagCampagneDTO {
  id: string
  naam: string
  jaar: number
  status: string
  fase: CampagneFase
  aanvraagDeadline: string
  uitkomstDeadline: string | null
  doelperiodeStart: string | null
  doelperiodeEind: string | null
  maxPeriodes: number
  maxLosseDagen: number
  spelregels: VakantieSpelregelDTO[]
  /** Extra campagnetekst van HR (platte tekst, alinea's). */
  campagneTekst: string | null
  /** Nu indienen is na de deadline (de aanvraag wordt als te laat gemarkeerd). */
  nuTeLaat: boolean
  collectief: boolean
}

export interface VakantieAanvraagKeuzesDTO {
  /** De keuzes die op de peildatum (vandaag) gelden, uit de contractmutatie-tijdlijn. */
  huidig: OpbouwKeuzeDTO | null
  /** De keuzes die zonder actie op 1 januari van het campagnejaar gelden ("keuzes houden"). */
  houden: OpbouwKeuzeDTO
  /** Sinds wanneer (ingangsdatum van de contractmutatie). */
  sinds: string | null
  atvMogelijk: boolean
  atvTonen: boolean
  atvUrenPerJaar: number
  /** Gaat de keuze in per 1 januari van het campagnejaar? */
  ingangsdatum: string
  /** 1 december vóór het campagnejaar (ISO), of null als die dag voorbij is (A9). */
  tvtDecemberDatum: string | null
}

export interface VakantieAanvraagAdviesDTO {
  categorie: AdviesCategorie
  kop: string
  advies: OpbouwKeuzeDTO
  wijzigt: { atv: boolean; overuren: boolean; reistijd: boolean }
  redenen: Array<AdviesRedenDTO & { tekst: string }>
  graden: {
    voldoendeInDienst: boolean
    maandenInDienst: number
    totaalOpgenomen: number
    gewoon: { beschikbaar: number; gebruikt: number; graad: number | null; overEind: number }
    atv: { beschikbaar: number; gebruikt: number; graad: number | null; overEind: number }
    tvtWerk: { beschikbaar: number; gebruikt: number; graad: number | null; overEind: number }
    tvtReis: { beschikbaar: number; gebruikt: number; graad: number | null; overEind: number }
  }
}

export interface VakantieAanvraagDeelnameDTO {
  id: string
  status: string
  keuzeBesluit: CampagneKeuzeBesluit | null
  /** De gekozen keuzes (na indienen, of het concept). */
  gekozen: OpbouwKeuzeDTO | null
  /** Welke knop de medewerker koos (advies, zelf of houden), ook in een concept; null = nog niet. */
  keuzeInvoer: CampagneKeuzeInput | null
  mutatieVerzoek: { id: string; status: string; reviewNote: string | null } | null
  teLaat: boolean
  ingediendOp: string | null
  /** Het saldo is gewijzigd sinds het indienen (advies opnieuw bekijken). */
  saldoGewijzigdOp: string | null
  geenVerlof: boolean
}

export interface VakantieAanvraagDTO {
  campagne: VakantieAanvraagCampagneDTO
  jaaroverzicht: JaaroverzichtDTO
  keuzes: VakantieAanvraagKeuzesDTO
  advies: VakantieAanvraagAdviesDTO
  doorrekeningInvoer: DoorrekeningInvoerDTO
  scenarios: { houden: Doorrekening2027DTO; advies: Doorrekening2027DTO }
  deelname: VakantieAanvraagDeelnameDTO | null
  wensen: CampagneWensDTO[]
}

// ─── Backoffice: beoordelen ──────────────────────────────────────────────────

export const campagneWensGoedkeurenSchema = z
  .object({ opmerking: z.string().trim().max(1000).optional() })
  .strict()

/** Afkeuren: reden verplicht (keuzelijst + toelichting, verplicht bij Overig). */
export const campagneWensAfkeurenSchema = z
  .object({
    redenSoort: z.enum(CAMPAGNE_AFWIJS_REDEN, { error: 'Kies een reden' }),
    toelichting: z.string().trim().max(900, 'De toelichting is te lang (maximaal 900 tekens)').optional(),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.redenSoort === 'OVERIG' && (d.toelichting ?? '').length < 3) {
      ctx.addIssue({ code: 'custom', path: ['toelichting'], message: 'Licht de reden toe (minimaal 3 tekens)' })
    }
  })
export type CampagneWensAfkeurenInput = z.infer<typeof campagneWensAfkeurenSchema>

export const campagneWensInOverlegSchema = z
  .object({
    notitie: z.string({ error: 'Geef een notitie' }).trim().min(3, 'Geef een notitie (minimaal 3 tekens)').max(900),
  })
  .strict()
export type CampagneWensInOverlegInput = z.infer<typeof campagneWensInOverlegSchema>

export const CAMPAGNE_BULK_ACTIES = ['GOEDKEUREN', 'AFKEUREN', 'IN_OVERLEG'] as const
export type CampagneBulkActie = (typeof CAMPAGNE_BULK_ACTIES)[number]

export const campagneWensenBulkSchema = z
  .object({
    voorkeurIds: z.array(idParam('voorkeurId')).min(1, 'Selecteer minstens één wens').max(200),
    actie: z.enum(CAMPAGNE_BULK_ACTIES),
    redenSoort: z.enum(CAMPAGNE_AFWIJS_REDEN).optional(),
    toelichting: z.string().trim().max(900).optional(),
    notitie: z.string().trim().max(900).optional(),
  })
  .strict()
  .superRefine((d, ctx) => {
    if (d.actie === 'AFKEUREN' && !d.redenSoort) ctx.addIssue({ code: 'custom', path: ['redenSoort'], message: 'Kies een reden' })
    if (d.actie === 'AFKEUREN' && d.redenSoort === 'OVERIG' && (d.toelichting ?? '').length < 3) {
      ctx.addIssue({ code: 'custom', path: ['toelichting'], message: 'Licht de reden toe (minimaal 3 tekens)' })
    }
    if (d.actie === 'IN_OVERLEG' && (d.notitie ?? '').length < 3) {
      ctx.addIssue({ code: 'custom', path: ['notitie'], message: 'Geef een notitie (minimaal 3 tekens)' })
    }
  })
export type CampagneWensenBulkInput = z.infer<typeof campagneWensenBulkSchema>

export interface CampagneWensenBulkUitkomstDTO {
  verwerkt: number
  overgeslagen: Array<{ voorkeurId: string; medewerkerNaam: string; reden: string }>
}

/** "Uitkomst versturen": onbeoordeelde wensen blokkeren, tenzij ze expliciet als "in overleg" meegaan. */
export const campagneUitkomstVersturenSchema = z
  .object({
    onbeoordeeldAlsInOverleg: z.boolean().default(false),
    bevestig: z.literal(true, { error: 'Bevestig het versturen' }),
  })
  .strict()
export type CampagneUitkomstVersturenInput = z.infer<typeof campagneUitkomstVersturenSchema>

export interface CampagneUitkomstPreviewDTO {
  medewerkers: number
  wensen: number
  goedgekeurd: number
  afgekeurd: number
  inOverleg: number
  wachtlijst: number
  onbeoordeeld: number
  /** Al eerder (vervroegd) verstuurd: gaan niet opnieuw. */
  alVerstuurd: number
  kanVersturen: boolean
  /** Zin voor de bevestiging: "48 medewerkers, 171 wensen: 150 goedgekeurd, 14 afgekeurd, 7 in overleg". */
  samenvatting: string
}

export interface CampagneUitkomstResultaatDTO {
  medewerkersGemeld: number
  verlofaanvragen: number
  onderVoorbehoud: number
  mislukt: Array<{ voorkeurId: string; medewerkerNaam: string; reden: string }>
}

export const campagneAanvragenFilterSchema = z.object({
  status: z.enum(CAMPAGNE_WENS_STATUS).optional(),
  alleenConflicten: queryBoolean().optional(),
  alleenLosseDagen: queryBoolean().optional(),
  alleenGewijzigd: queryBoolean().optional(),
  alleenTeLaat: queryBoolean().optional(),
  afdelingId: idParam('afdelingId').optional(),
  klantId: idParam('klantId').optional(),
  /** ISO-week, bijvoorbeeld 2027-W31. */
  week: z.string().regex(/^\d{4}-W\d{2}$/, 'Kies een week als 2027-W31').optional(),
  zoek: z.string().trim().max(100).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(5).max(100).default(25),
})
export type CampagneAanvragenFilter = z.infer<typeof campagneAanvragenFilterSchema>

export interface CampagneWensBeoordelingDTO {
  id: string
  soort: CampagneWensSoort
  omschrijving: string
  startDatum: string
  eindDatum: string
  dagdeel: CampagneDagdeel | null
  weken: string[]
  uren: number
  prioriteit: number | null
  saldoNa: number | null
  status: CampagneWensStatus
  besluitReden: string | null
  afkeurReden: CampagneAfwijsReden | null
  ingediendOp: string | null
  teLaat: boolean
  uitkomstVerstuurd: boolean
  /** Bezetting per klant in de weken van de wens (n / max tegelijk). */
  bezetting: Array<{ klantId: string; klantNaam: string; week: string; aantal: number; maxTegelijk: number; conflict: boolean }>
  conflict: boolean
  /** Eigen wens van de beoordelaar (4-ogen) of na de einddatum: niet goed te keuren. */
  eigenWens: boolean
  naEinddatum: boolean
  /** Een losse of halve dag vóór 1 april die (nog) niet is verstuurd: vervroegd versturen kan. */
  vervroegdMogelijk: boolean
  vervangtVoorkeurId: string | null
  leaveRequestId: string | null
}

export interface CampagneAanvraagMedewerkerDTO {
  deelnameId: string
  employeeId: string
  naam: string
  afdeling: string | null
  status: string
  keuzeBesluit: CampagneKeuzeBesluit | null
  aantalWensen: number
  uren: number
  /** Verwacht saldo eind van het campagnejaar (live, met het gekozen of gehouden scenario). */
  saldoEindJaar: number | null
  teLaat: boolean
  gewijzigd: boolean
  saldoGewijzigd: boolean
  reconciliatieVerschil: number
  /** Sluit het jaaroverzicht per potje? null = onbekend (de berekening lukte niet). */
  reconciliatieSluitend: boolean | null
  /** Per potje dat niet sluit het verschil (2 decimalen), bv. "ATV +4,00 u; Wettelijk verlof -4,00 u". */
  reconciliatieDetail: string | null
  wensen: CampagneWensBeoordelingDTO[]
}

export interface CampagneTellersDTO {
  doelgroep: number
  ingediend: number
  nogNiet: number
  wensen: number
  losseDagen: number
  wekenBovenDrempel: number
  adviesGevolgd: number
  zelfIngesteld: number
  gehouden: number
  geenKeuze: number
  gewijzigdNaIndienen: number
  saldoGewijzigd: number
  teLaat: number
}

/** De kop van de collectieve beoordeling (fase, tellers): licht, voor elk tabblad. */
export interface CampagneBeoordelingKopDTO {
  fase: CampagneFase
  collectief: boolean
  aanvraagDeadline: string
  uitkomstDeadline: string | null
  uitkomstVerstuurdOp: string | null
  tellers: CampagneTellersDTO
  bezettingOnbekend: boolean
}

export interface CampagneAanvragenOverzichtDTO {
  fase: CampagneFase
  collectief: boolean
  uitkomstDeadline: string | null
  uitkomstVerstuurdOp: string | null
  tellers: CampagneTellersDTO
  medewerkers: CampagneAanvraagMedewerkerDTO[]
  bezettingOnbekend: boolean
  meta: { page: number; pageSize: number; total: number }
}

export interface CampagneKeuzeRijDTO {
  deelnameId: string
  employeeId: string
  naam: string
  huidig: OpbouwKeuzeDTO | null
  gekozen: OpbouwKeuzeDTO | null
  advies: OpbouwKeuzeDTO | null
  keuzeBesluit: CampagneKeuzeBesluit | null
  afwijkendVanAdvies: boolean
  mutatieVerzoek: { id: string; status: string; reviewNote: string | null; ingangsdatum: string | null; tvtEerderPer: string | null } | null
}

export interface CampagneGewijzigdRijDTO {
  medewerkerNaam: string
  employeeId: string
  nieuw: CampagneWensBeoordelingDTO
  oud: CampagneWensBeoordelingDTO
  oudWasGoedgekeurd: boolean
}

export interface CampagneAuditRegelDTO {
  id: string
  tijd: string
  actie: string
  actieLabel: string
  actorNaam: string | null
  medewerkerNaam: string | null
  omschrijving: string
}

/** De matrix week x klant (§E20): per klant hoeveel er per week (mogelijk) weg zijn. */
export interface CampagneBezettingsMatrixDTO {
  weken: string[]
  drempel: number
  klanten: Array<{
    klantId: string
    klantNaam: string
    monteurs: number
    maxTegelijk: number
    perWeek: Record<string, number>
  }>
  onbekend: boolean
}

/** De overleg-lijst van een week en klant (§E20), in voorrangsvolgorde (A5). */
export interface CampagneOverlegLijstDTO {
  week: string
  klantId: string | null
  klantNaam: string | null
  maxTegelijk: number | null
  regels: Array<CampagneWensBeoordelingDTO & { medewerkerNaam: string; employeeId: string; vasteMonteurBijKlant: boolean }>
}

/** Instellingen van de campagne die HR/ADMIN beheert (§5). */
export const campagneVakantieInstellingenSchema = z
  .object({
    spelregels: z.string().trim().max(4000).nullable().optional(),
    uitkomstDeadline: z.string().datetime().nullable().optional(),
    maxLosseDagen: z.number().int().min(0).max(40).optional(),
  })
  .strict()
export type CampagneVakantieInstellingenInput = z.infer<typeof campagneVakantieInstellingenSchema>
